/**
 * Affichage du verdict d'éligibilité d'une aide — T-085.
 *
 * Le verdict vient du CODE (`source_verdict === "code"`). Tout enregistrement
 * sans cette marque est une ancienne analyse : on le dit, on ne l'affiche pas
 * comme un verdict. Le score numérique des anciennes analyses n'est jamais montré.
 * Un montant n'est affiché que s'il a été calculé par le code ; aucune règle de
 * cumul ni de plafond n'est affichée (aucune source écrite).
 */
import { Info } from "lucide-react";

export const MONTANT_NON_CALCULABLE = "Barème non disponible, calcul impossible";

export const VERDICTS = {
  eligible:    { label: "Éligible",         bg: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)", border: "var(--oh-stat-green-border)" },
  indetermine: { label: "À confirmer",      bg: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", border: "var(--oh-stat-amber-border)" },
  non_eligible: { label: "Non éligible",    bg: "var(--oh-stat-red-bg)",   color: "var(--oh-stat-red-text)",   border: "var(--oh-stat-red-border)" },
};

const ANCIENNE = { label: "Ancienne analyse — résultat non contrôlé", bg: "var(--oh-stat-slate-bg)", color: "var(--oh-stat-slate-text)", border: "var(--oh-stat-slate-border)" };

export function verdictDuCode(entree) {
  return entree?.source_verdict === "code" ? (entree.verdict || null) : null;
}

export function VerdictBadge({ entree, testId }) {
  const v = verdictDuCode(entree);
  const cfg = (v && VERDICTS[v]) || ANCIENNE;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium"
      style={{ background: cfg.bg, color: cfg.color, border: `0.5px solid ${cfg.border}` }}
      data-testid={testId || "verdict-badge"}
      data-verdict={v || "ancienne_analyse"}
    >
      {cfg.label}
    </span>
  );
}

export function MontantCode({ montant, formatMontant }) {
  if (montant == null || montant === 0) {
    return (
      <span className="text-xs" style={{ color: "var(--oh-text-muted)" }} data-testid="montant-non-calculable">
        {MONTANT_NON_CALCULABLE}
      </span>
    );
  }
  return (
    <span className="text-xs font-medium" style={{ color: "var(--oh-text)" }} data-testid="montant-calcule">
      {formatMontant(montant)}
    </span>
  );
}

export function RegleEtManquants({ entree }) {
  const regle = entree?.regle_citee || [];
  const manquants = entree?.manquants || [];
  if (verdictDuCode(entree) == null) return null;
  return (
    <div className="space-y-1" data-testid="verdict-detail">
      {regle.length > 0 && (
        <p className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>
          Règle appliquée : {regle.join(" ; ")}
        </p>
      )}
      {manquants.length > 0 && (
        <p className="text-xs" style={{ color: "var(--oh-stat-amber-text)" }} data-testid="verdict-manquants">
          Informations manquantes : {manquants.join(", ")}
        </p>
      )}
    </div>
  );
}

export function EtiquetteBrouillon({ demande }) {
  if (!demande?.contenu) return null;
  const nonPerso = !!demande.brouillon_non_personnalise;
  return (
    <p
      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg"
      style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)", border: "0.5px solid var(--oh-stat-blue-border)" }}
      data-testid="etiquette-brouillon"
    >
      <Info className="w-3 h-3" />
      {nonPerso
        ? "Brouillon non personnalisé — à compléter et à relire"
        : "Brouillon préparé par l'assistant — à relire"}
    </p>
  );
}
